import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { locationApi } from '../services/api';
import { Location } from '../types';
import { useAuth } from '../context/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

// Create context for selected location
interface LocationContextType {
  selectedLocationId: number | null;
  setSelectedLocationId: (id: number | null) => void;
  locations: Location[];
}

const LocationContext = createContext<LocationContextType>({
  selectedLocationId: null,
  setSelectedLocationId: () => {},
  locations: []
});

export const useLocationContext = () => useContext(LocationContext);

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { employee, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMyRecordsOpen, setIsMyRecordsOpen] = useState(false);
  const myRecordsRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  // Fetch locations on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const locationsData = await locationApi.getAll();
        setLocations(locationsData);
        if (locationsData.length > 0 && !selectedLocationId) {
          // Try to get from localStorage first
          const savedLocationId = localStorage.getItem('selectedLocationId');
          if (savedLocationId && locationsData.some(loc => loc.id === Number(savedLocationId))) {
            setSelectedLocationId(Number(savedLocationId));
          } else {
            setSelectedLocationId(locationsData[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };
    fetchLocations();
  }, []);

  // Save selected location to localStorage
  useEffect(() => {
    if (selectedLocationId) {
      localStorage.setItem('selectedLocationId', selectedLocationId.toString());
    }
  }, [selectedLocationId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (myRecordsRef.current && !myRecordsRef.current.contains(event.target as Node)) {
        setIsMyRecordsOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    if (isMyRecordsOpen || isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMyRecordsOpen, isProfileOpen]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Extract tenant name from hostname (similar to old project)
  const hostname = window.location.hostname;
  const tenantName = hostname.includes('localhost') || hostname.includes('replit') 
    ? 'Development' 
    : hostname.split('.')[0].charAt(0).toUpperCase() + hostname.split('.')[0].slice(1);

  const navigation = [
    { name: "Dashboard", href: "/", icon: "📊" },
    { name: "Employees", href: "/employees", icon: "👥" },
    { name: "Scheduling", href: "/scheduling", icon: "📅" },
    { name: "Attendance", href: "/attendance", icon: "⏰" },
    { name: "Timecards", href: "/timecards", icon: "📝" },
  ];

  return (
    <div className="h-screen bg-neutral-50 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <nav className="bg-white shadow-lg border-b border-neutral-200 relative flex-shrink-0">
        <div className="w-full max-w-[82%] mx-auto px-6">
          {/* First Line: Logo and Profile */}
          <div className="flex justify-between items-center h-28 py-1">
            <div className="flex items-center space-x-4">
              <div className="h-24 w-auto flex items-center">
                <img src="/logo.png" alt="Staffly" className="h-16 w-auto" />
              </div>
              <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 border border-blue-200 rounded-full">
                {tenantName}
              </span>
            </div>

            {/* Profile Dropdown */}
            <div className="flex items-center space-x-3">
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-neutral-100 rounded-lg p-2 transition-colors"
                >
                  <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {employee?.firstName?.[0]?.toUpperCase() || employee?.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <span className="hidden md:block text-sm font-medium text-neutral-700">
                    {employee ? `${employee.firstName} ${employee.lastName}` : 'User'}
                  </span>
                </button>
                
                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-neutral-200 z-50">
                    <div className="py-1">
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        ⚙️ Settings
                      </Link>
                      <Link
                        to="/leave-policies"
                        className="flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        📋 Leave Policies
                      </Link>
                      <Link
                        to="/my-account"
                        className="flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        👤 My Account
                      </Link>
                      <Link
                        to="/holiday-admin"
                        className="flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        🏖️ Holiday Admin
                      </Link>
                      <Link
                        to="/incident-admin"
                        className="flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        ⚠️ Incident Admin
                      </Link>
                      <Link
                        to="/task-admin"
                        className="flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        📋 Task Admin
                      </Link>
                      <Link
                        to="/job-monitor"
                        className="flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        ⚡ Job Admin
                      </Link>
                      <hr className="my-1" />
                      <button
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-neutral-100"
                        onClick={async () => {
                          setIsProfileOpen(false);
                          await logout();
                          navigate('/login');
                        }}
                      >
                        🚪 Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Second Line: Navigation, Clock In/Out, and Location */}
          <div className="flex justify-between items-center pb-4 border-t border-neutral-200 pt-4">
            {/* Navigation Menu and Clock In/Out */}
            <div className="flex items-center space-x-10">
              {/* Desktop Navigation */}
              <div className="hidden md:flex md:space-x-8 md:items-center">
                {navigation.map((item) => (
                  <Link key={item.name} to={item.href}>
                    <div
                      className={`${
                        isActive(item.href)
                          ? "text-blue-600 border-b-2 border-blue-600"
                          : "text-neutral-600 hover:text-neutral-900"
                      } px-1 pb-2 text-sm font-medium transition-colors cursor-pointer flex items-center space-x-2`}
                    >
                      <span>{item.icon}</span>
                      <span>{item.name}</span>
                    </div>
                  </Link>
                ))}
                {/* My Records Dropdown */}
                <div className="relative" ref={myRecordsRef}>
                  <button
                    onClick={() => setIsMyRecordsOpen(!isMyRecordsOpen)}
                    className={`${
                      isActive('/my-incidents')
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-neutral-600 hover:text-neutral-900"
                    } px-1 pb-2 text-sm font-medium transition-colors cursor-pointer flex items-center space-x-2`}
                  >
                    <span>📋</span>
                    <span>My Records</span>
                    <span className={`text-xs transition-transform ${isMyRecordsOpen ? 'rotate-180' : ''}`}>▼</span>
                  </button>
                  {isMyRecordsOpen && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-neutral-200 z-50">
                      <div className="py-1">
                        <Link
                          to="/my-incidents"
                          className={`flex items-center px-4 py-2 text-sm ${
                            isActive('/my-incidents')
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-neutral-700 hover:bg-neutral-100'
                          }`}
                          onClick={() => setIsMyRecordsOpen(false)}
                        >
                          ⚠️ My Incidents
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
                {/* Reports Link */}
                <Link
                  to="/reports"
                  className={`${
                    location.pathname.startsWith('/reports')
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-neutral-600 hover:text-neutral-900"
                  } px-1 pb-2 text-sm font-medium transition-colors flex items-center space-x-2`}
                >
                  <span>📈</span>
                  <span>Reports</span>
                </Link>
              </div>

              {/* Clock In/Out Button */}
              <button className="hidden sm:flex px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 transition-colors">
                Clock In/Out
              </button>

              {/* Notifications */}
              <button className="relative p-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-md transition-colors">
                🔔
                <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  3
                </span>
              </button>
            </div>

            {/* Location Selector */}
            <div className="flex items-center space-x-2">
              <span className="text-neutral-600">📍</span>
              <select 
                value={selectedLocationId || ''}
                onChange={(e) => {
                  const newLocationId = e.target.value ? Number(e.target.value) : null;
                  setSelectedLocationId(newLocationId);
                }}
                className="w-52 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {locations.length === 0 ? (
                  <option value="">Loading...</option>
                ) : (
                  <>
                    <option value="">All Locations</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </>
                )}
              </select>
            </div>
          </div>
        </div>
        
        {/* Mobile menu button */}
        <button
          className="md:hidden absolute top-6 right-6 p-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-md transition-colors"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? '✕' : '☰'}
        </button>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-neutral-200 absolute top-full left-0 right-0 bg-white shadow-lg z-50">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navigation.map((item) => (
                <Link key={item.name} to={item.href}>
                  <div
                    className={`${
                      isActive(item.href)
                        ? "bg-blue-50 border-blue-500 text-blue-700"
                        : "border-transparent text-neutral-600 hover:bg-neutral-50 hover:border-neutral-300 hover:text-neutral-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors cursor-pointer flex items-center space-x-2`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                </Link>
              ))}
              <div className="pl-3 pr-4 py-2">
                <div className="text-sm font-medium text-neutral-700 mb-1">My Records</div>
                <Link
                  to="/my-incidents"
                  className={`block pl-4 pr-4 py-2 text-sm rounded ${
                    isActive('/my-incidents')
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-neutral-600 hover:bg-neutral-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  ⚠️ My Incidents
                </Link>
              </div>
              <Link
                to="/reports"
                className={`block pl-3 pr-4 py-2 text-sm font-medium rounded ${
                  location.pathname.startsWith('/reports')
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-neutral-600 hover:bg-neutral-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                📈 Reports
              </Link>
              <button
                className="w-full mt-4 px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 transition-colors"
                onClick={() => {
                  // Add clock in/out logic
                  setIsMobileMenuOpen(false);
                }}
              >
                Clock In/Out
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto min-h-0">
        <LocationContext.Provider value={{ selectedLocationId, setSelectedLocationId, locations }}>
          {children}
        </LocationContext.Provider>
      </main>
    </div>
  );
};

export default Layout;